import { cn } from "@/lib/utils";
import { Eye, EyeOff, XCircle } from "lucide-react";
import React, { useState, FocusEvent, useEffect, useRef } from "react";

interface FloatingLabelInputProps {
  type: string;
  label: string;
  value: string;
  required?: boolean;
  placeholder?: string;
  errorMessage?: string;
  readonly?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onClear?: () => void;
  onEnter?: () => void;
  maxLength?: number;
}

const FloatingLabelInput: React.FC<FloatingLabelInputProps> = ({
  type,
  label,
  value,
  required,
  placeholder,
  errorMessage,
  maxLength,
  readonly,
  onChange,
  onFocus,
  onBlur,
  onClear,
  onEnter,
}) => {
  const [active, setActive] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // useEffect(() => {
  //   if (value) {
  //     setActive(true);
  //   }
  // }, []);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setActive(true);
    if (onFocus) {
      onFocus(event);
    }
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    // if (inputValue.length === 0) {
      setActive(false);
    // }
    if (onBlur) {
      onBlur(event);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    onChange(event);
  };

  const handleClear = () => {
    setInputValue("");
    if (onClear) {
      onClear();
    }
  };

  return (
    <div className="relative w-full">
      <div
        className={cn(
          "flex justify-center items-center relative h-[52px] rounded-[4px] ",
          errorMessage
            ? "border-2 border-[#F87171] dark:border-[#FCA5A5]"
            : active
              ? "border-2 border-primary-500 dark:border-primary-300 bg-primary-50 dark:bg-primary-900"
              : inputValue.length > 0
                ? "border-2 border-neutral-200 dark:border-neutral-200 bg-primary-50 dark:bg-primary-900"
                : "border border-neutral-600 dark:border-neutral-400"
        )}
        onClick={() => inputRef.current?.focus()} // Ensures clicking anywhere focuses the input
      >
        <input
          ref={inputRef}
          className={cn(
            "outline-none w-full rounded text-base transition-all duration-200 ease-in-out px-2 bg-transparent",
            inputValue.length > 0 || active ? "pt-3" : ""
          )}
          id={label}
          type={showPwd ? "text" : type.toLowerCase()}
          value={inputValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={readonly}
          maxLength={maxLength}
          onKeyPress={(event) => {
            if (event.key === "Enter" && onEnter) {
              onEnter();
            }
          }}
          aria-label={placeholder || label}
        />
        <label
          className={cn(
            "absolute left-2 transition-all duration-200 ease-in-out cursor-text", // Added cursor-text
            inputValue.length > 0 || active
              ? "text-xs top-[6px]"
              : "text-sm top-[14px]",
            errorMessage
              ? "text-[#F87171] dark:text-[#FCA5A5]"
              : inputValue.length > 0 || active
                ? "!text-primary-500 dark:!text-primary-300"
                : "text-neutral-600 dark:text-neutral-600"
          )}
          onClick={() => inputRef.current?.focus()} // Ensures label click focuses input
        >
          {placeholder || label}{" "}
          {required && <span className="text-[#F87171]">*</span>}
        </label>
        {inputValue.length > 0 && type !== "Password" && !readonly && (
          <div
            className="mr-2 cursor-pointer"
            aria-label="Clear"
            role="button"
            tabIndex={0}
            onClick={() => {
              setActive(false);
              handleClear();
            }}
          >
            <XCircle
              size={24}
              strokeWidth={1.5}
              aria-hidden="true"
              className={
                errorMessage
                  ? "text-[#F87171] dark:text-[#FCA5A5]"
                  : "text-primary-500 dark:text-primary-200"
              }
            />
          </div>
        )}
        {inputValue.length > 0 && type === "Password" && (
          <div
            className="mr-2 cursor-pointer"
            onClick={() => setShowPwd(!showPwd)}
          >
            {!showPwd ? (
              <Eye
                size={24}
                strokeWidth={1.5}
                className={
                  errorMessage
                    ? "text-[#F87171] dark:text-[#FCA5A5]"
                    : "text-primary-500 dark:text-primary-200"
                }
              />
            ) : (
              <EyeOff
                size={24}
                strokeWidth={1.5}
                className={
                  errorMessage
                    ? "text-[#F87171] dark:text-[#FCA5A5]"
                    : "text-primary-500 dark:text-primary-200"
                }
              />
            )}
          </div>
        )}
      </div>
      {errorMessage && (
        <div className="text-[#F87171] dark:text-[#F87171] text-xs mt-2">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default FloatingLabelInput;
